import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import axios from 'axios';
import { Plus, Trash2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { useAuth } from '../../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * DropHistoryManager - Manages drop schedule and reminder subscribers
 * Extracted from AdminDashboard.js for maintainability
 */
export default function DropHistoryManager() {
  const { getAuthHeader } = useAuth();
  const [history, setHistory] = useState([]);
  const [currentDrop, setCurrentDrop] = useState(null);
  const [reminderSubs, setReminderSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newDrop, setNewDrop] = useState({ lot_number: '', scheduled_time: '' });

  useEffect(() => {
    fetchHistory();
    fetchCurrentDrop();
    fetchReminderSubs();
  }, []);

  const fetchHistory = async () => {
    try {
      const response = await axios.get(`${API}/drop/history`);
      setHistory(response.data || []);
    } catch (error) {
      console.error('Failed to fetch drop history:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCurrentDrop = async () => {
    try {
      const response = await axios.get(`${API}/drop/status`);
      if (response.data?.scheduled_drop_time) {
        setCurrentDrop(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch current drop:', error);
    }
  };

  const fetchReminderSubs = async () => {
    try {
      const headers = getAuthHeader();
      const response = await axios.get(`${API}/admin/drop/reminders`, { headers });
      setReminderSubs(response.data || []);
    } catch (error) {
      console.error('Failed to fetch reminder subscribers:', error);
    }
  };

  const handleAddDrop = async () => {
    if (!newDrop.lot_number || !newDrop.scheduled_time) {
      toast.error('Please fill in drop name and date/time');
      return;
    }
    try {
      const entry = {
        lot_number: newDrop.lot_number,
        scheduled_time: new Date(newDrop.scheduled_time).toISOString()
      };
      
      await axios.post(`${API}/admin/drop/history/manual`, entry, {
        headers: getAuthHeader()
      });
      
      toast.success('Drop added to schedule');
      setNewDrop({ lot_number: '', scheduled_time: '' });
      setShowAddForm(false);
      fetchHistory();
    } catch (error) {
      toast.error('Failed to add drop');
    }
  };

  const handleDeleteDrop = async (dropId) => {
    if (!window.confirm('Delete this drop?')) return;
    try {
      await axios.delete(`${API}/admin/drop/history/${dropId}`, {
        headers: getAuthHeader()
      });
      toast.success('Drop removed');
      fetchHistory();
    } catch (error) {
      toast.error('Failed to delete drop');
    }
  };

  const formatDropDate = (isoString) => {
    if (!isoString) return '-';
    try {
      const date = new Date(isoString);
      const dayName = date.toLocaleDateString('en-US', { weekday: 'long', timeZone: 'America/New_York' }).toUpperCase();
      const month = (date.getMonth() + 1);
      const day = date.getDate();
      const year = date.getFullYear().toString().slice(-2);
      const hours = date.getHours();
      const minutes = date.getMinutes().toString().padStart(2, '0');
      const ampm = hours >= 12 ? 'PM' : 'AM';
      const hour12 = hours % 12 || 12;
      return `${dayName} ${month}/${day}/${year} ${hour12}:${minutes} ${ampm} EST`;
    } catch {
      return isoString;
    }
  };

  const isPending = (isoString) => {
    if (!isoString) return false;
    return new Date(isoString) > new Date();
  };

  const getCountdown = (isoString) => {
    if (!isoString) return '';
    const diff = new Date(isoString) - new Date();
    if (diff <= 0) return '';
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const mins = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    if (days > 0) return `${days}d ${hours}h`;
    if (hours > 0) return `${hours}h ${mins}m`;
    return `${mins}m`;
  };

  return (
    <div className="space-y-6 mt-6">
      {/* Current Scheduled Drop - from drop config */}
      {currentDrop?.scheduled_drop_time && isPending(currentDrop.scheduled_drop_time) && (
        <div className="border-2 border-tiffany-500/50 bg-tiffany-500/10 p-4 max-w-lg" data-testid="current-scheduled-drop">
          <div className="flex items-center justify-between mb-2">
            <span className="px-2 py-0.5 bg-tiffany-500 text-black text-[10px] font-mono font-bold">
              NEXT DROP
            </span>
            <span className="text-tiffany-400 font-mono text-sm">
              {getCountdown(currentDrop.scheduled_drop_time)}
            </span>
          </div>
          <p className="font-mono text-lg text-tiffany-500 font-bold">
            {currentDrop.drop_name || 'UNNAMED DROP'}
          </p>
          <p className="font-mono text-sm text-zinc-400">
            {formatDropDate(currentDrop.scheduled_drop_time)}
          </p>
        </div>
      )}

      {/* Drop Schedule */}
      <div className="border border-zinc-800 p-6 space-y-4 max-w-lg" data-testid="drop-history-manager">
        <div className="flex items-center justify-between">
          <h3 className="font-sans text-lg text-white">DROP SCHEDULE</h3>
          <Button
            onClick={() => setShowAddForm(!showAddForm)}
            className="h-8 px-3 bg-zinc-800 text-tiffany-500 hover:bg-zinc-700 rounded-none text-xs"
          >
            <Plus className="w-4 h-4 mr-1" />
            ADD DROP
          </Button>
        </div>
        
        {showAddForm && (
          <div className="p-4 bg-zinc-900/50 border border-zinc-700 space-y-3">
            <Input
              placeholder="DROP 1"
              value={newDrop.lot_number}
              onChange={(e) => setNewDrop(d => ({ ...d, lot_number: e.target.value }))}
              className="bg-zinc-900 border-zinc-800 text-white rounded-none font-mono"
            />
            <Input
              type="datetime-local"
              value={newDrop.scheduled_time}
              onChange={(e) => setNewDrop(d => ({ ...d, scheduled_time: e.target.value }))}
              className="bg-zinc-900 border-zinc-800 text-white rounded-none font-mono [color-scheme:dark]"
            />
            <div className="flex gap-2">
              <Button
                onClick={handleAddDrop}
                className="flex-1 h-8 bg-tiffany-500 text-black hover:bg-tiffany-400 rounded-none text-xs"
              >
                ADD DROP
              </Button>
              <Button
                onClick={() => setShowAddForm(false)}
                className="h-8 px-3 bg-zinc-800 text-zinc-400 hover:bg-zinc-700 rounded-none text-xs"
              >
                CANCEL
              </Button>
            </div>
          </div>
        )}
        
        {loading ? (
          <p className="font-mono text-xs text-zinc-500">Loading...</p>
        ) : history.length === 0 ? (
          <p className="font-mono text-xs text-zinc-500">No drops scheduled</p>
        ) : (
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {history.map((drop) => {
              const pending = isPending(drop.scheduled_time);
              const countdown = getCountdown(drop.scheduled_time);
              return (
                <div 
                  key={drop.id}
                  className={`flex items-center justify-between p-3 border ${pending ? 'bg-amber-500/10 border-amber-500/30' : 'bg-zinc-900/50 border-zinc-800'}`}
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      {pending && (
                        <span className="px-2 py-0.5 bg-amber-500 text-black text-[10px] font-mono font-bold">
                          PENDING
                        </span>
                      )}
                      <span className={`font-mono text-sm font-bold ${pending ? 'text-amber-400' : 'text-tiffany-500'}`}>
                        {drop.lot_number || 'UNNAMED'}
                      </span>
                    </div>
                    <p className="font-mono text-xs text-zinc-400 mt-1">
                      {formatDropDate(drop.scheduled_time || drop.opened_at)}
                      {pending && countdown && (
                        <span className="ml-2 text-amber-400">({countdown})</span>
                      )}
                    </p>
                  </div>
                  <Button
                    onClick={() => handleDeleteDrop(drop.id)}
                    className="h-7 w-7 p-0 bg-transparent hover:bg-red-500/20 text-zinc-500 hover:text-red-400"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Reminder Subscribers */}
      <div className="border border-zinc-800 p-6 space-y-4 max-w-lg" data-testid="reminder-subscribers">
        <div className="flex items-center justify-between">
          <h3 className="font-sans text-lg text-white">10-MIN REMINDER LIST</h3>
          <span className="px-2 py-1 bg-zinc-800 text-tiffany-500 text-xs font-mono">
            {reminderSubs.length} SUBSCRIBED
          </span>
        </div>
        
        {reminderSubs.length === 0 ? (
          <p className="font-mono text-xs text-zinc-500">No one subscribed to reminders yet</p>
        ) : (
          <div className="space-y-1 max-h-64 overflow-y-auto">
            {reminderSubs.map((sub, idx) => (
              <div 
                key={sub.id || idx}
                className="flex items-center justify-between py-2 px-3 bg-zinc-900/30 text-xs font-mono"
              >
                <span className="text-zinc-300 truncate">{sub.email}</span>
                <span className={`text-xs ${sub.reminder_sent ? 'text-green-500' : 'text-tiffany-500'}`}>
                  {sub.reminder_sent ? 'SENT' : 'SUBSCRIBED'}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
